import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import ConfirmDialog from "../../components/ConfirmDialog";
import Notification from "../../components/Notification";
import { formatToDayMonthYear } from "../../utils/TextUtils";
import { allowedToView } from "../../utils/PermissionUtils";

function Documents() {
    const [data, setData] = useState({
        documents: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    });
    const [documentTypes, setDocumentTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const location = useLocation();

    const [filters, setFilters] = useState({
        search: "",
        document_type_id: "",
        status: "",
        page: 1,
        per_page: 15
    });

    const [pendingSearch, setPendingSearch] = useState("");

    const [isDropdownOpen, setIsDropdownOpen] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });

    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => allowedToView(userRole, ['admin', 'superadmin', 'missions-coordinator']);
    const navigate = useNavigate();

    // Fetch document types once on mount
    useEffect(() => {
        const fetchDocumentTypes = async () => {
            try {
                const token = localStorage.getItem("access_token");
                const response = await fetch(`${API_URL}/documents/types`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });

                if (response.ok) {
                    const typesData = await response.json();
                    setDocumentTypes(typesData.data || typesData);
                } else {
                    throw new Error("Failed to load document types");
                }
            } catch (error) {
                console.error("Document Types Fetch Error:", error);
            }
        };

        fetchDocumentTypes();
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("access_token");
            const queryParams = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) queryParams.append(key, value);
            });

            const response = await fetch(`${API_URL}/documents?${queryParams}`, {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                const documentsData = await response.json();
                setData({
                    documents: documentsData.data,
                    current_page: documentsData.current_page,
                    last_page: documentsData.last_page,
                    per_page: documentsData.per_page,
                    total: documentsData.total
                });
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else {
                throw new Error("Failed to fetch documents");
            }
        } catch (error) {
            setError("An error occurred while fetching data.");
            console.error("Fetch Data Error:", error);
        } finally {
            setLoading(false);
        }
    }, [filters, navigate, location]);

    useEffect(() => {
        fetchData();
    }, [filters.document_type_id, filters.status, filters.page, filters.per_page, fetchData]);

    const toggleDropdown = (index) => {
        setIsDropdownOpen(isDropdownOpen === index ? null : index);
    };

    const handleDeleteClick = (document) => {
        setSelectedDocument(document);
        setIsConfirmDialogOpen(true);
    };

    const confirmDeleteDocument = async () => {
        if (!selectedDocument) return;

        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/documents/${selectedDocument.id}`, {
                method: 'DELETE',
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                setNotification({ type: "success", message: 'Document deleted successfully' });
                fetchData();
            } else if (response.status === 401) {
                navigate("/logout", { state: { from: location } });
            } else if (response.status === 404) {
                setNotification({ type: "danger", message: 'Document not found.' });
            } else {
                const errorData = await response.json();
                setNotification({ type: "danger", message: errorData.message || 'There was an error.' });
            }
        } catch (error) {
            setNotification({ type: 'danger', message: 'An error occurred while deleting the document.' });
            console.error("Delete Error:", error);
        } finally {
            setIsConfirmDialogOpen(false);
            setSelectedDocument(null);
        }
    };

    const cancelDeleteDocument = () => {
        setIsConfirmDialogOpen(false);
        setSelectedDocument(null);
    };

    const handleDownload = async (doc) => {
        try {
            const token = localStorage.getItem("access_token");
            const response = await fetch(`${API_URL}/documents/${doc.id}/download`, {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;


                const contentDisposition = response.headers.get('Content-Disposition');
                let filename = doc.name;
                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
                    if (filenameMatch && filenameMatch.length === 2) {
                        filename = filenameMatch[1];
                    }
                }

                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

                setNotification({ type: "success", message: 'Document download started' });
            } else {
                setNotification({ type: "danger", message: 'Failed to download document' });
            }
        } catch (error) {
            console.error("Download Error:", error);
            setNotification({ type: 'danger', message: 'An error occurred while downloading the document.' });
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    const handleSearchInputChange = (e) => {
        setPendingSearch(e.target.value);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setFilters(prev => ({ ...prev, search: pendingSearch, page: 1 }));
    };

    const handleClearSearch = () => {
        setPendingSearch("");
        setFilters(prev => ({
            ...prev,
            search: "",
            page: 1
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handlePerPageChange = (e) => {
        const perPage = parseInt(e.target.value);
        setFilters(prev => ({
            ...prev,
            per_page: perPage,
            page: 1
        }));
    };

    const getFileIcon = (fileName) => {
        if (!fileName) return "file";

        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "file-pdf";
            case 'doc':
            case 'docx':
                return "file-word";
            default:
                return "file";
        }
    };

    const getFileTypeText = (fileName) => {
        if (!fileName) return "Unknown";

        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return "PDF";
            case 'doc':
                return "Word (DOC)";
            case 'docx':
                return "Word (DOCX)";
            default:
                return extension?.toUpperCase() || "File";
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isDropdownOpen !== null && !event.target.closest('.dropdown.is-active')) {
                setIsDropdownOpen(null);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [isDropdownOpen]);

    return (
        <div>
            <main>
                <nav className="level">
                    <div className="level-left">
                        <div className="level-item">
                            <p className="title">Documents</p>
                        </div>
                    </div>
                    <div className="level-right">
                        {canEdit(userRole) && (
                            <p className="level-item">
                                <Link to="add" className="button primary-bg">
                                    <i className="fa fa-plus" aria-hidden="true"></i> &nbsp; New Document
                                </Link>
                            </p>
                        )}
                    </div>
                </nav>

                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSearchSubmit}>
                    <div className="columns">
                        <div className="column">
                            <div className="field">
                                <label className="label">Search</label>
                                <div className="field has-addons">
                                    <div className="control is-expanded">
                                        <input
                                            className="input"
                                            type="text"
                                            name="search"
                                            placeholder="Search by name, description, file name, or document type"
                                            value={pendingSearch}
                                            onChange={handleSearchInputChange}
                                        />
                                    </div>
                                    <div className="control">
                                        <button
                                            type="button"
                                            className="button is-light"
                                            onClick={handleClearSearch}
                                            disabled={!pendingSearch && !filters.search}
                                        >
                                            <span className="icon">
                                                <i className="fas fa-times"></i>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="column is-narrow">
                            <div className="field">
                                <label className="label">&nbsp;</label>
                                <div className="field is-grouped">
                                    <div className="control">
                                        <button
                                            type="submit"
                                            className="button primary-outline-button"
                                        >
                                            Search
                                        </button>
                                    </div>
                                    <div className="control">
                                        <button
                                            type="button"
                                            className="button is-light"
                                            onClick={handleClearSearch}
                                            disabled={!pendingSearch && !filters.search}
                                        >
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="columns">
                    <div className="column">
                        <div className="field">
                            <label className="label">Document Type</label>
                            <div className="control select is-fullwidth">
                                <select name="document_type_id" value={filters.document_type_id} onChange={handleFilterChange}>
                                    <option value="">All Types</option>
                                    {documentTypes.map(type => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="column">
                        <div className="field">
                            <label className="label">Items per page</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select value={filters.per_page} onChange={handlePerPageChange}>
                                        {[10, 15, 25, 50].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="has-text-centered">
                        <p>Loading documents...</p>
                        <progress className="progress is-small is-primary" max="100">15%</progress>
                    </div>
                ) : error ? (
                    <p className="has-text-danger">{error}</p>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table is-striped is-fullwidth">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Document Type</th>
                                        <th>File Type</th>
                                        <th>Status</th>
                                        <th>Uploaded</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.documents.length > 0 ? (
                                        data.documents.map((document, index) => (
                                            <tr key={document.id}>
                                                <td>{(data.current_page - 1) * data.per_page + index + 1}</td>
                                                <td>
                                                    <Link to={`${document.id}/details`} className="no_deco_link">
                                                        <span className="default-color has-text-weight-semibold">
                                                            {document.name}
                                                        </span>
                                                    </Link>
                                                </td>

                                                <td>
                                                    <span className="tag is-info is-light">
                                                        {document.document_type?.name}
                                                    </span>
                                                </td>
                                                <td>

                                                    <span className="icon">
                                                        <i className={`fas fa-${getFileIcon(document.file_name)}`}></i>
                                                    </span>
                                                    <span>{getFileTypeText(document.file_name)}</span>
                                                    <span
                                                        className="tag is-light is-success is-clickable"
                                                        onClick={() => handleDownload(document)}
                                                        title={`Download ${document.name}`}
                                                    > <small>Download</small></span>
                                                </td>
                                                <td>
                                                    <span className={`tag ${document.status === 'Active' ? 'is-success' : 'is-danger'}`}>
                                                        {document.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {document.created_at && formatToDayMonthYear(document.created_at)}
                                                </td>
                                                <td>
                                                    <div className={`dropdown is-right ${isDropdownOpen === index ? 'is-active' : ''}`}>
                                                        <div className="dropdown-trigger">
                                                            <button
                                                                className="button is-white"
                                                                aria-haspopup="true"
                                                                aria-controls="dropdown-menu"
                                                                onClick={() => toggleDropdown(index)}
                                                            >
                                                                <span className="icon">
                                                                    <i className="fas fa-ellipsis-v"></i>
                                                                </span>
                                                            </button>
                                                        </div>
                                                        <div className="dropdown-menu" id="dropdown-menu" role="menu">
                                                            <div className="dropdown-content">
                                                                <button
                                                                    className="dropdown-item"
                                                                    onClick={() => handleDownload(document)}
                                                                >
                                                                    <span className="icon">
                                                                        <i className="fas fa-download"></i>
                                                                    </span>
                                                                    <span>Download</span>
                                                                </button>
                                                                <Link to={`${document.id}/details`} className="dropdown-item">
                                                                    <span className="icon">
                                                                        <i className="fas fa-eye"></i>
                                                                    </span>
                                                                    <span>View Details</span>
                                                                </Link>
                                                                {canEdit(userRole) && (
                                                                    <>
                                                                        <Link to={`${document.id}/update`} className="dropdown-item">
                                                                            <span className="icon">
                                                                                <i className="fas fa-edit"></i>
                                                                            </span>
                                                                            <span>Edit</span>
                                                                        </Link>
                                                                        <button
                                                                            className="dropdown-item"
                                                                            onClick={() => handleDeleteClick(document)}
                                                                        >
                                                                            <span className="icon">
                                                                                <i className="fas fa-trash-alt"></i>
                                                                            </span>
                                                                            <span>Delete</span>
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="has-text-centered">
                                                No documents found matching your criteria
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {data.total > 0 && (
                            <nav className="pagination is-centered" role="navigation" aria-label="pagination">
                                <button
                                    className="pagination-previous"
                                    onClick={() => handlePageChange(data.current_page - 1)}
                                    disabled={data.current_page === 1}
                                >
                                    Previous
                                </button>
                                <button
                                    className="pagination-next"
                                    onClick={() => handlePageChange(data.current_page + 1)}
                                    disabled={data.current_page === data.last_page}
                                >
                                    Next
                                </button>
                                <ul className="pagination-list">
                                    {Array.from({ length: data.last_page }, (_, i) => i + 1).map(page => (
                                        <li key={page}>
                                            <button
                                                className={`pagination-link ${data.current_page === page ? 'is-current' : ''}`}
                                                aria-label={`Go to page ${page}`}
                                                onClick={() => handlePageChange(page)}
                                            >
                                                {page}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        )}
                    </>
                )}
                <ConfirmDialog
                    isOpen={isConfirmDialogOpen}
                    theme="is-danger"
                    title="Delete Confirmation"
                    message={`Are you sure you want to delete the document "${selectedDocument?.name}"? This action cannot be undone.`}
                    onConfirm={confirmDeleteDocument}
                    onCancel={cancelDeleteDocument}
                    isLoading={false}
                />
            </main>
        </div>
    );
}

export default Documents;